import React, { useState, useEffect, useRef, useMemo } from "react";

/*
 * ProjectCard — karta projektu v portfolio mřížce.
 *
 * Klíčová vylepšení:
 *   1) Fotky se preloadují v pozadí (stagger ~600 ms na fotku).
 *   2) Cyklus přeskočí na fotku, která je už načtená.
 *   3) První fotka má loading="eager" — vidíš ji okamžitě.
 *   4) Interval 8 s, vypnuto na mobilu a pro prefers-reduced-motion.
 */
function ProjectCard({ project, index, onOpen }) {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const photos = useMemo(() => project.photo || [], [project.photo]);

  // Stav načtených fotek — v ref, aby nerestartoval interval cyklu.
  const loadedRef = useRef({ 0: true });

  // Preload zbylých fotek v pozadí (stagger)
  useEffect(() => {
    if (photos.length < 2) return;
    const timers = [];
    photos.forEach((p, i) => {
      if (i === 0) return;
      const t = setTimeout(() => {
        const img = new Image();
        img.onload = () => { loadedRef.current[i] = true; };
        img.onerror = () => { loadedRef.current[i] = true; };
        img.src = p.src;
      }, 600 * i);
      timers.push(t);
    });
    return () => timers.forEach(clearTimeout);
  }, [photos]);

  // Cyklus přepínání s crossfade
  useEffect(() => {
    if (paused || photos.length < 2) return;
    const noMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const isNarrow = window.matchMedia("(max-width: 768px)").matches;
    if (noMotion || isNarrow) return;

    const INTERVAL = 8000;
    const stagger = (index * 900) % INTERVAL;

    const advance = () => {
      setCurrent((c) => {
        const total = photos.length;
        for (let step = 1; step <= total; step++) {
          const candidate = (c + step) % total;
          if (loadedRef.current[candidate]) return candidate;
        }
        return c;
      });
    };

    let intervalId;
    const startId = setTimeout(() => {
      advance();
      intervalId = setInterval(advance, INTERVAL);
    }, stagger);

    return () => {
      clearTimeout(startId);
      if (intervalId) clearInterval(intervalId);
    };
  }, [index, paused, photos.length]);

  return (
    <button
      type="button"
      className="project-card"
      onClick={() => onOpen(index)}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="project-card__media">
        {photos.map((p, i) => (
          <img
            key={i}
            src={p.src}
            alt={"Projekt: " + project.location}
            loading={i === 0 ? "eager" : "lazy"}
            decoding={i === 0 ? "sync" : "async"}
            fetchpriority={i === 0 ? "high" : "low"}
            onLoad={() => { loadedRef.current[i] = true; }}
            onError={() => { loadedRef.current[i] = true; }}
            className={"project-card__img " + (i === current ? "is-active" : "is-prev")}
          />
        ))}
        <span className="project-card__count">
          {photos.length}{" "}
          {photos.length === 1 ? "fotka" : photos.length < 5 ? "fotky" : "fotek"}
        </span>
      </div>
      <div className="project-card__body">
        <h3 className="project-card__title">{project.location}</h3>
      </div>
    </button>
  );
}

export default ProjectCard;
